import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import authService from '../../services/authService';

const PrivateRoute = ({ element, roles }) => {
  const { user, loading: authLoading } = useAuth();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        if (user) {
          if (!cancelled) {
            setIsAuthenticated(true);
            setSessionChecked(true);
          }
          return;
        }

        const authenticated = authService.isAuthenticated();
        if (!authenticated) {
          if (!cancelled) {
            setIsAuthenticated(false);
            setSessionChecked(true);
          }
          return;
        }

        const response = await authService.verifySession();
        if (!cancelled) {
          if (response?.authenticated) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
            authService.logout();
          }
          setSessionChecked(true);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (!cancelled) {
          setIsAuthenticated(false);
          authService.logout();
          setSessionChecked(true);
        }
      }
    };

    if (!authLoading) {
      checkAuth();
    }

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  if (authLoading || !sessionChecked) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] bg-light px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand mx-auto mb-4" />
          <p className="text-secondary text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && roles.length > 0) {
    const hasAccess = authService.hasAnyRole(roles);

    if (!hasAccess) {
      const currentUser = user || authService.getCurrentUser();
      return (
        <div className="flex items-center justify-center min-h-[50vh] bg-light p-4">
          <div className="text-center w-full">
            <div className="bg-white p-6 sm:p-8 rounded-card shadow-card max-w-md mx-auto">
              <div className="text-danger text-4xl sm:text-5xl mb-4">🚫</div>
              <p className="text-danger text-lg sm:text-xl font-semibold mb-2">Access Denied</p>
              <p className="text-secondary text-sm sm:text-base mt-2 mb-4">
                You do not have permission to access this page.
              </p>
              <div className="bg-gray-50 rounded-card p-3 sm:p-4 mb-4 text-left">
                <p className="text-xs sm:text-sm text-secondary mb-1">
                  <span className="font-medium text-gray-700">Required role:</span> {roles.join(' or ')}
                </p>
                <p className="text-xs sm:text-sm text-secondary">
                  <span className="font-medium text-gray-700">Your role:</span> {currentUser?.role || 'Unknown'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => window.history.back()}
                className="w-full sm:w-auto mt-2 px-6 py-2.5 bg-brand text-white rounded-btn hover:bg-brand-hover transition-colors text-sm sm:text-base font-medium"
              >
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  return element;
};

export default PrivateRoute;
